import type { FilterItem } from '@/components/ui/custom/ComplexFilter/types';
import { getFilterValues } from '@/hooks/useTableFilters';

import type { FilterDefinition } from './ComplexFilterCombobox';

interface Handlers {
  setFilter: (key: string, values: string[]) => void;
  removeFilter: (key: string) => void;
}

/**
 * Adapter between `useTableFilters` state + `FilterDefinition[]` and the `FilterBadge[]`
 * shape that `ComplexFilterBar` consumes. Each active filter becomes one pill that shows
 * the first selected value (with "+N" for additional selections) and wires remove/change.
 *
 * Callers whose options list doesn't cover every possible persisted value should make sure
 * the definition's `options` array is exhaustive — the fallback here is the raw value, which
 * is intentional (it's still better than an empty pill) but ideally never hit.
 */
export const buildFilterBadges = (
  filterDefinitions: FilterDefinition[],
  filters: FilterItem[],
  { setFilter, removeFilter }: Handlers
) =>
  filterDefinitions.flatMap((def) => {
    const values = getFilterValues(filters, def.id);
    if (values.length === 0) {
      return [];
    }
    const labelFor = (v: string) => def.options.find((o) => o.value === v)?.label ?? v;
    return [
      {
        id: def.id,
        icon: def.icon,
        title: def.label,
        text:
          values.length === 1
            ? labelFor(values[0])
            : `${labelFor(values[0])} +${values.length - 1}`,
        onRemove: () => removeFilter(def.id),
        filterDefinition: { type: def.type, options: def.options },
        // Checkbox filters are multi-select (string[]); radio/text are single-value (string).
        // Hand the popover the shape its primitives expect, not the raw storage shape.
        value: def.type === 'checkbox' ? values : values[0],
        onValueChange: (value: string | string[] | undefined) => {
          const normalized = Array.isArray(value) ? value : value ? [value] : [];
          if (normalized.length === 0) {
            removeFilter(def.id);
            return;
          }
          setFilter(def.id, normalized);
        },
      },
    ];
  });
